import React, { useCallback, useEffect, useState } from "react"
import moment from 'moment'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faXmark, faUser, faUserTie } from "@fortawesome/free-solid-svg-icons"

import LoadingInner from 'components/LoadingInner'

import api from 'lib/api'
import { discectError, ucwords } from "lib/utils"

import styles from './styles.module.scss'

const Chat = ({ className, agency, surety }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [agentMessages, setAgentMessages] = useState(null)
  const [error, setError] = useState(null)
  const [viewDeletes, setViewDeletes] = useState([])

  const _fetchNotes = useCallback(async () => {
    setIsLoading(true)
    try {
      if (agency && surety) {
        const ownerId = surety?.owner?.id

        let messages = []
        const results = await api.agencies.fetchTimeline(surety?.id, agency?.id, agency?.owner?.id, ownerId, {}) 

        for(let i=0; i<results.chat.length;i++){
          if (
            results.chat[i].type === 'CHAT' && 
            (results.chat[i].userType === 'SURETY' || results.chat[i].userType === 'AGENCY')
            ) { 
              messages.push(results.chat[i])
            }
        }
        setAgentMessages(messages)
      }
    } catch(e) {
      setError(discectError(e))
    }
    setIsLoading(false)
  }, [agency, surety])

  useEffect(() => {
    if (!agentMessages && agency?.id) _fetchNotes()
  }, [agency, agentMessages, _fetchNotes])

  const _deleteTimelineNote = async noteId => {
    setError(null)
    setIsLoading(true)  
    try {
      await api.agencies.deleteTimelineNote(agency?.id, surety?.id, agency.employee_id, 'agent', { id: noteId })
      await _fetchNotes()
    } catch(e) {
      setError(discectError(e))
    }
    setIsLoading(false)
  }

  const _saveNote = async e => {
    e.preventDefault()

    setError(null)
    
    if (message !== '') {
      setIsLoading(true)

      let params = {
        "type": "CHAT",
        "userType": "AGENCY",
        "note": message,
        "data": {}
      }
      
      try {
        await api.agencies.saveTimelineNote(surety?.id, agency?.id, agency?.owner?.id, params)
        await _fetchNotes()
        setMessage('')
      } catch(e) {
        setError(discectError(e))
      }
      setIsLoading(false)
    }
  }

  const _toggleDeletedView = noteId => {
    let newViewDeletes = [...viewDeletes]
    if (newViewDeletes.indexOf(noteId) >= 0) {
      newViewDeletes = newViewDeletes.filter(itm => itm !== noteId)
    } else {
      newViewDeletes.push(noteId)
    }
    setViewDeletes(newViewDeletes)
  }

  const _getForm = () => {
    const { employee: { nameFirst, nameLast } } = agency
    let initials = `${nameFirst.substr(0,1)}${nameLast.substr(0,1)}`

    return (
      <>
        <header>
          <div className={styles.notes__avatar}><strong>{initials}</strong></div>
          <form onSubmit={_saveNote}>
            <input type="text" placeholder="Add Note" value={message.text} onChange={e => setMessage(e.target.value)} />
            <input type="submit" value="Save" className="butt butt--navy" />
          </form>
        </header>
        {error && <p className="error">{error}</p>}
      </>
    )
  }

  const _getDaysSince = ts => {
    const _getOrdinal = n => n > 1 ? 's' : ''
    const now = moment()
    const date = moment.unix(ts / 1000)
    const seconds = now.diff(date, "seconds")
    if (seconds < 60) {
      return `${seconds} second${_getOrdinal(seconds)} ago`
    }
    const minutes = now.diff(date, "minutes")
    if (minutes < 60) {
      return `${minutes} minute${_getOrdinal(minutes)} ago`
    }
    const hours = now.diff(date, "hours")
    if (hours < 24) {
      return `${hours} hour${_getOrdinal(hours)} ago`
    }
    const days = now.diff(date, "days")
    if (days <= 30) {
      return `${days} day${_getOrdinal(days)} ago`
    }
    const weeks = now.diff(date, "weeks")
    if (weeks <= 8) {
      return `${weeks} week${_getOrdinal(weeks)} ago`
    }
    const months = now.diff(date, "months")
    if (months < 12) {
      return `${months} month${_getOrdinal(months)} ago`
    }
    const years = now.diff(date, "years")
    return `${years} year${_getOrdinal(years)} ago`
  }

  let classes = styles.notes
  if (className) classes = `${classes} ${className}`

  return (
    <div className={classes}>
      <h5 className="rad">Contract Chat</h5>
      {_getForm()}
      <div className={styles.notes__container}>
        {(agentMessages?.length > 0) && agentMessages?.slice().reverse().map((itm, key) => {
          const isDeletetedViewable = viewDeletes.indexOf(itm.id) >= 0

          if (itm.isDeleted && !isDeletetedViewable) {
            return (
              <div className={`${styles.note} ${styles.note__deleted}`} key={key}>
                <div className={styles.note__top}>
                  <div className={`${styles.note__icon} ${styles.note__icon__red}`}>
                    <FontAwesomeIcon icon={faXmark} />
                  </div>
                  <div className={styles.note__content}>
                    <p>Note Deleted</p>
                    <div className={styles.note__actions}>
                      <button onClick={() => _toggleDeletedView(itm.id)}>View</button>
                      <button className={styles.note__actions__red} onClick={() => _deleteTimelineNote(itm.id)}>Un-Delete</button>
                    </div>
                  </div>
                </div>
                <div className={styles.note__btm}>
                  <div className={styles.note__meta}>
                    <span>{_getDaysSince(itm.timestamp)}</span>
                    <span>{ucwords(`${itm.data.noteTaker.nameFirst} ${itm.data.noteTaker.nameLast}`.toLowerCase())}</span>
                  </div>
                  <div className={styles.note__actions}>
                    <button className={styles.note__actions__red} onClick={() => _deleteTimelineNote(itm.id)}>Delete</button>
                  </div>
                </div>
              </div>
            )
          }
          if(itm.userType === 'AGENCY') {
            return (
              <div className={styles.noteLeft} key={key}>
                <div className={styles.noteLeft__top}>
                  <div className={styles.noteLeft__content}>
                    <p>{itm.message}</p>
                  </div>
                  <div className={styles.noteLeft__icon}>
                    <FontAwesomeIcon icon={faUserTie} />
                  </div>
                </div>
                <div className={styles.noteLeft__btm}>
                  <div className={styles.noteLeft__meta}>
                    <span>{_getDaysSince(itm.timestamp)}</span>
                    <span>User Note</span>
                    {/* <span>{ucwords(`${itm.data.noteTaker.nameFirst} ${itm.data.noteTaker.nameLast}`.toLowerCase())}</span> */}
                  </div>
                  <div className={styles.noteLeft__actions}>
                    {isDeletetedViewable && <button onClick={() => _toggleDeletedView(itm.id)}>Hide</button>}
                  </div>
                </div>
              </div>
            )
          }
          return (
            <div className={styles.noteRight} key={key}>
              <div className={styles.noteRight__top}>
                <div className={styles.noteRight__content}>
                  <p>{itm.message}</p>
                </div>
                <div className={styles.noteRight__icon}>
                  <FontAwesomeIcon icon={faUser} />
                </div>
              </div>
              <div className={styles.noteRight__btm}>
                <div className={styles.noteRight__meta}>
                  <span>{_getDaysSince(itm.timestamp)}</span>
                  <span>Surety</span>
                  {/* <span>{ucwords(`${itm.data.noteTaker.nameFirst} ${itm.data.noteTaker.nameLast}`.toLowerCase())}</span> */}
                </div>
                <div className={styles.noteRight__actions}>
                  {isDeletetedViewable && <button onClick={() => _toggleDeletedView(itm.id)}>Hide</button>}
                  {/* {isDisplayDelete && <button className={styles.note__actions__red} onClick={() => _deleteTimelineNote(itm.id)}>Delete</button>} */}
                </div>
              </div>
            </div>
          )
        })}
        {isLoading && <LoadingInner className={styles.loading} />}
      </div>
    </div>
  )
}

export default Chat